import {
     AfterViewInit,
     Component,
     ElementRef,
     OnDestroy,
     OnInit,
     ViewChild,
} from '@angular/core';
import {
     PlanningCoach,
     PlanningGroup,
     PlanningHeaderDate,
     User,
} from '../../../typings';
import * as moment from 'moment';
import { Moment } from 'moment';
import { Router } from '@angular/router';
import { WsService } from '../../services/ws/ws.service';
import {
     ISchedulerBrowseFilters,
     IWsPlanningBrowsePlanningItemRes,
} from '../../services/ws/ws.typings';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { MatDialog } from '@angular/material/dialog';
import { BookComponent } from '../book/book.component';
import { map, takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { DataService } from '../../services/data.service';
import { CoachPickerComponent } from '../coach-picker/coach-picker.component';
import { ArrayToDictionaryBy } from '../../utils/array-to-dictionary';
import { SpinnerMessageService } from '../../services/spinner-message.service';
import { TV_SCREEN } from '../../constants';
import { scheduleWarning } from '../../utils/schedule-warning';

export interface ITaskEmployee {
     idEmp: number;
     label: string;
     idActivity: number;
}

export interface ITaskAccess {
     idArticle: number;
     label: string;
     idActivity: number;
}

export interface ITaskRoom {
     idRoom: number;
     label: string;
     idActivity: number;
}

export interface IQueueList {
     cascadeAction: number;
     cascadeInterval: number;
     autoDisable: number;
     joinQueueByEmployee: boolean;
     joinQueueByClient: boolean;
}

export interface ITask {
     id: number;
     authorized: boolean;
     line: number;
     label: string;
     color: string;
     column: number;
     description: string;
     idGroup: number;
     access: ITaskAccess[];
     employee: ITaskEmployee[];
     room: ITaskRoom[];
     cover: string;
     photo: string;
     video: string;
     maxPlaces: number;
     queueList: IQueueList;
     maxBookablePlaces: number;
     multiClub: boolean;
}

interface IScheduler {
     [date: string]: {
          [hour: number]: IWsPlanningBrowsePlanningItemRes[];
     };
}

export enum PERIOD {
     DAWN = 'DAWN',
     MORNING = 'MORNING',
     MIDDAY = 'MIDDAY',
     AFTERNOON = 'AFTERNOON',
     EVENING = 'EVENING',
}

@Component({
     selector: 'app-planning',
     templateUrl: './planning.component.html',
     styleUrls: ['./planning.component.scss'],
})
export class PlanningComponent implements AfterViewInit, OnInit, OnDestroy {
     @ViewChild('planningContent') planningContent: ElementRef;
     typeRowExpanded = false;
     destroy$$ = new Subject();
     isTvScreen = TV_SCREEN;
     filterMode = window.app.conf.ui?.planningFilterMode;
     scheduler: IScheduler;
     dates: PlanningHeaderDate[] = [];
     currentWeekOffset = 0;
     groupList: PlanningGroup[] = [];
     weekdayLabels = moment.weekdays(false); // 0 = Dimanche
     user: User = { fullName: '' };
     isFirstDayToday = true;
     currentPos = null;
     coachList: PlanningCoach[] = [];
     selectedCoaches: PlanningCoach[] = [];
     selectedCoachesIds: number[] = [];
     selectedCoachesStr = '';
     selectedGroups = [];
     selectedTasks = [];
     taskList: ITask[] = [];
     planning$ = new Subscription();
     taskDictionary: { [key: string]: ITask };
     groupDictionary: { [key: string]: PlanningGroup };
     isInit = true;
     tvPopup: {
          clickedElement: any;
          info: {
               scheduledTask: any;
               task: any;
          };
     };
     periodCollapse = {
          DAWN: false,
          MORNING: false,
          MIDDAY: false,
          AFTERNOON: false,
          EVENING: false,
     };
     periodLocale = {
          DAWN: window.appLang.DAWN, // SUNRISE
          MORNING: window.appLang.MORNING,
          MIDDAY: window.appLang.MIDDAY,
          AFTERNOON: window.appLang.AFTERNOON,
          EVENING: window.appLang.EVENING,
     };
     periodList = [];
     hours: {
          [key: string]: Array<number>;
     } = {};
     selectedDate = null;
     appCfg = window.app;
     loading = false;

     constructor(
          private router: Router,
          private ws: WsService,
          private toastrService: ToastrService,
          private loader: NgxSpinnerService,
          private dialog: MatDialog,
          private dataService: DataService,
          private spinner: SpinnerMessageService
     ) {
          spinner.reset();
          this.loader.show();
          if (TV_SCREEN) {
               scheduleWarning('00:00:00', () => {
                    window.location.reload();
               });
               scheduleWarning('12:00:00', () => {
                    window.location.reload();
               });
          }
     }

     hourVisibility = new Array(24).fill(0);
     hourVisibilityTotal = 0;
     hourVisibilityMin = 0;
     hourVisibilityMax = 0;

     isSelected(date) {
          return date === this.selectedDate;
     }

     toggleData(data) {
          if (this.selectedDate === data) {
               this.selectedDate = null;
          } else {
               this.selectedDate = data;
          }
     }

     onWeekChange() {
          this.isFirstDayToday = this.currentWeekOffset === 0;
     }

     /**
      * Return dates for current week offset
      */
     generateDates(offset: number) {
          const startDate = moment().add(offset, 'week');
          const dates = [0, 1, 2, 3, 4, 5, 6].map((day) => {
               return moment(startDate).add(day, 'day');
          });
          return dates;
     }

     ngOnInit() {
          const dateList = this.generateDates(this.currentWeekOffset);
          this.dates = this.dateListToDateHeader(dateList);
          this.onWeekChange();

          if (TV_SCREEN) {
               this.initTvScreen();
          } else {
               this.initDefault();
          }
     }

     ngOnDestroy(): void {
          this.destroy$$.next(null);
          this.destroy$$.unsubscribe();
     }

     ngAfterViewInit() {}

     async initTvScreen() {
          const idCenter = window.app.club.id;
          this.coachList = await this.ws.planning.coachesList({
               idCenter,
          });
          this.taskList = await this.ws.planning.taskList({
               idCenter,
          });
          this.groupList = await this.ws.planning.groupList({
               idCenter,
               onlyActive: true,
          });
          this.groupDictionary = ArrayToDictionaryBy(this.groupList, 'id');
          this.taskDictionary = ArrayToDictionaryBy(this.taskList, 'id');
          this.fetchPlanning({
               load: true,
          });
          setTimeout(() => {
               // hide popup on scroll
               $('.activities-content [scrollviewport]').on('scroll', () => {
                    this.tvPopup = null;
               });

               $(window).on('resize', () => {
                    this.tvPopup = null;
               });
          }, 500);
     }

     initDefault() {
          this.dataService.coaches$
               .pipe(takeUntil(this.destroy$$))
               .subscribe((data) => {
                    this.coachList = data || [];
               });
          this.dataService.tasks$
               .pipe(takeUntil(this.destroy$$))
               .subscribe((data) => {
                    this.taskList = data || [];
                    if (this.filterMode === 'by_task') {
                         this.taskDictionary = ArrayToDictionaryBy(data, 'id');
                    }
               });
          this.dataService.groups$
               .pipe(takeUntil(this.destroy$$))
               .subscribe((data) => {
                    this.groupList = data || [];
                    if (this.filterMode !== 'by_task') {
                         this.groupDictionary = ArrayToDictionaryBy(data, 'id');
                    }
               });
          this.fetchPlanning({
               load: true,
          });
     }

     filterTasks() {
          const filterCoaches = this.selectedCoachesIds.length >= 1;
          const filterGroups = this.selectedGroups.length >= 1;
          const filterTasks = this.selectedTasks.length >= 1;
          const selectedCoachesIds = this.selectedCoachesIds;
          const selectedGroupIds = this.selectedGroups;
          return map(
               (data: {
                    [key: string]: {
                         [key: number]: IWsPlanningBrowsePlanningItemRes[];
                    };
               }) => {
                    if (!data) {
                         return {};
                    }
                    if (!filterCoaches && !filterGroups && !filterTasks) {
                         return data;
                    } else {
                         const output = {};
                         // Loop over dates
                         for (const prop in data) {
                              if (data.hasOwnProperty(prop)) {
                                   output[prop] = output[prop] || {};
                                   // Loop over hours
                                   for (const prop2 in data[prop]) {
                                        if (data[prop].hasOwnProperty(prop2)) {
                                             output[prop][prop2] =
                                                  output[prop][prop2] || [];
                                             // Loop over scheduledTasks inside a date and of course inside a particular hour
                                             data[prop][prop2].forEach(
                                                  (scheduledTask) => {
                                                       let matchCoaches = true;
                                                       let matchTask = true;
                                                       let matchGroup = true;

                                                       if (filterCoaches) {
                                                            if (
                                                                 scheduledTask.idEmployee !=
                                                                      null &&
                                                                 scheduledTask.idEmployee >=
                                                                      0
                                                            ) {
                                                                 matchCoaches =
                                                                      selectedCoachesIds.indexOf(
                                                                           scheduledTask.idEmployee
                                                                      ) >= 0;
                                                            } else {
                                                                 matchCoaches = false;
                                                            }
                                                       }

                                                       // Check if previous value is valid
                                                       if (
                                                            matchCoaches &&
                                                            filterGroups
                                                       ) {
                                                            matchGroup =
                                                                 scheduledTask.idGroup !=
                                                                      null &&
                                                                 scheduledTask.idGroup >=
                                                                      0
                                                                      ? selectedGroupIds.indexOf(
                                                                             scheduledTask.idGroup
                                                                        ) >= 0
                                                                      : false;
                                                       }

                                                       // We just check for filters if the matchCoaches is true
                                                       if (
                                                            matchCoaches &&
                                                            matchGroup &&
                                                            filterTasks
                                                       ) {
                                                            matchTask =
                                                                 scheduledTask.idActivity !=
                                                                      null &&
                                                                 scheduledTask.idActivity >=
                                                                      0
                                                                      ? this.selectedTasks.indexOf(
                                                                             scheduledTask.idActivity
                                                                        ) >= 0
                                                                      : false;
                                                       }

                                                       if (
                                                            matchCoaches &&
                                                            matchGroup &&
                                                            matchTask
                                                       ) {
                                                            output[prop][
                                                                 prop2
                                                            ].push(
                                                                 scheduledTask
                                                            );
                                                       }
                                                  }
                                             );
                                        }
                                   }
                              }
                         }
                         return output;
                    }
               }
          );
     }

     week(operation) {
          if (this.loading) {
               return;
          }
          const nextOffset = this.currentWeekOffset + operation;
          if (nextOffset >= 0 && nextOffset <= 2) {
               this.currentWeekOffset = nextOffset;
               this.fetchPlanning({ load: true });
          }
     }

     fetchPlanning(options?: { load: boolean }) {
          this.planning$?.unsubscribe?.();
          this.planning$ = this.dataService.planning$
               .pipe(takeUntil(this.destroy$$), this.filterTasks())
               .subscribe((data: any) => {
                    this.scheduler = data;
                    this.isInit = false;
                    this.scrollToHour(new Date().getHours());
                    this.isInit = false;
                    const arr = [];
                    {
                         if (data) {
                              for (let o = 0; o <= 23; o++) {
                                   this.dates.forEach((key) => {
                                        if (data[key.fullDateStr]) {
                                             if (
                                                  arr[o] === void 0 ||
                                                  arr[o] <
                                                       data[key.fullDateStr][o]
                                                            .length
                                             ) {
                                                  arr[o] =
                                                       data[key.fullDateStr][
                                                            o
                                                       ].length;
                                             }
                                        }
                                   });
                              }
                         }
                    }
                    this.hourVisibility = arr;
                    this.hourVisibilityTotal = arr.filter((i) => i > 0).length;
                    this.hourVisibilityMin = void 0;
                    this.hourVisibilityMax = void 0;
                    const arr2 = [...arr];
                    let i = -1;
                    while (arr2.length) {
                         i++;
                         const v = arr2.shift();
                         if (v > 0) {
                              if (this.hourVisibilityMin === void 0) {
                                   this.hourVisibilityMin = i;
                              }
                              this.hourVisibilityMax = i;
                         }
                    }

                    this.hours[PERIOD.DAWN] = []; // <= 7
                    this.hours[PERIOD.MORNING] = []; // >= 8 && <= 11
                    this.hours[PERIOD.MIDDAY] = []; // >= 12 && <= 13
                    this.hours[PERIOD.AFTERNOON] = []; // >= 14 && <= 18
                    this.hours[PERIOD.EVENING] = []; // >= 19 && <= 23

                    {
                         for (let i = 0; i <= 23; i++) {
                              if (
                                   i >= this.hourVisibilityMin &&
                                   i <= this.hourVisibilityMax
                              ) {
                                   if (i >= 0 && i <= 7) {
                                        this.hours[PERIOD.DAWN].push(i);
                                   } else if (i >= 8 && i <= 11) {
                                        this.hours[PERIOD.MORNING].push(i);
                                   } else if (i >= 12 && i <= 13) {
                                        this.hours[PERIOD.MIDDAY].push(i);
                                   } else if (i >= 14 && i <= 18) {
                                        this.hours[PERIOD.AFTERNOON].push(i);
                                   } else if (i >= 19 && i <= 23) {
                                        this.hours[PERIOD.EVENING].push(i);
                                   }
                              }
                         }

                         this.periodList = [];
                         [
                              PERIOD.DAWN,
                              PERIOD.MORNING,
                              PERIOD.MIDDAY,
                              PERIOD.AFTERNOON,
                              PERIOD.EVENING,
                         ].forEach((period) => {
                              if (this.hours[period].length) {
                                   this.periodList.push(period);
                              }
                         });
                    }
               });

          if (options?.load) {
               let datesStr = '';
               const mom = moment().add(this.currentWeekOffset, 'week');
               const datesMom = [] as Moment[];
               for (let i = 0; i < 7; i++) {
                    if (i >= 1) {
                         mom.add(1, 'days');
                    }
                    datesMom.push(mom.clone());
                    const d = mom.format('YYYY-MM-DD');
                    datesStr += d + ',';
               }
               this.dateListToDateHeader(datesMom);
               this.ws.planning
                    .browsePlanning<IScheduler>({
                         dates: datesStr,
                         stackBy: 'date-hour',
                         idCenter: this.appCfg.club.id,
                         discardDeleted: true,
                         discardOverlapped: true,
                         onlyActive: true,
                    } as ISchedulerBrowseFilters)
                    .then((data) => {
                         // Object.keys(data).forEach(key => {
                         //   Object.keys(key).forEach(key2 => {
                         //     key2 = key2?.filter(item => item)
                         //   })
                         // })
                         // this.scheduler = data;options
                         this.loader.hide();
                         this.dataService.planning$ = data;
                    })
                    .catch((err) => {
                         this.toastrService.error(
                              window.appLang.GENERIC_ERROR_MSG
                         );
                         this.loader.hide();
                    });
          }
     }

     /**
      * Converts an array of dates into an `PlanningHeaderObject`
      */
     dateListToDateHeader(dateList: Array<Moment>): Array<PlanningHeaderDate> {
          return (this.dates = dateList.map((date, index) => {
               let weekDayLabelIndex = date.isoWeekday(); // 1=Monday .... 7 = Sunday
               weekDayLabelIndex =
                    weekDayLabelIndex >= 7 ? 0 : weekDayLabelIndex;
               return {
                    dateStr: date.format('DD MMM'),
                    weekDay: this.weekdayLabels[weekDayLabelIndex],
                    fullDateStr: date.format('YYYY-MM-DD'),
               } as PlanningHeaderDate;
          })); // as Array<PlanningHeaderDate>;
     }

     scrollInto(view) {
          if (this.planningContent?.nativeElement) {
               const d = this.planningContent.nativeElement.querySelector(
                    '[data-section="' + view + '"]'
               );
               d.scrollIntoView();
               this.currentPos = view;
          }
     }

     scrollToHour(hour) {
          if (this.planningContent?.nativeElement) {
               const d = this.planningContent.nativeElement.querySelector(
                    '[data-hour="' + hour + '"]'
               );
               d?.scrollIntoView();
          }
     }

     isInto(view) {
          return this.currentPos === view;
     }

     openCoachesPicker() {
          this.dialog
               .open(CoachPickerComponent, {
                    data: {
                         coachList: this.coachList,
                         selectedCoaches: this.selectedCoachesIds,
                    },
                    panelClass: 'dialog--no-padding',
               })
               .afterClosed()
               .subscribe((data) => {
                    if (data) {
                         this.selectedCoachesIds = data.map(
                              (d: PlanningCoach) => d.id
                         );
                         this.selectedCoaches = [].concat(data);
                         this.generateCoachesStr();
                         this.fetchPlanning();
                    }
               });
     }

     generateCoachesStr() {
          let coachStr = '';
          if (this.selectedCoaches[0]) {
               coachStr = this.selectedCoaches[0].fullName;
          }
          if (this.selectedCoaches[1]) {
               coachStr += ', ' + this.selectedCoaches[1].fullName;
          }
          if (this.selectedCoaches.length >= 3) {
               coachStr += ' et +' + (this.selectedCoaches.length - 2);
          }
          this.selectedCoachesStr = coachStr;
     }

     resetCoaches(options?: { fetch: boolean }) {
          this.selectedCoaches = [];
          this.selectedCoachesIds = [];
          if (options?.fetch) {
               this.fetchPlanning();
          }
     }

     toggleTask(task) {
          const pos = this.selectedTasks.indexOf(task.id);
          if (pos >= 0) {
               this.selectedTasks.splice(pos, 1);
          } else {
               this.selectedTasks.push(task.id);
          }
          this.fetchPlanning();
     }

     toggleGroup(group) {
          const groupPos = this.selectedGroups.indexOf(group.id);
          if (groupPos >= 0) {
               this.selectedGroups.splice(groupPos, 1);
          } else {
               this.selectedGroups.push(group.id);
          }
          this.fetchPlanning();
     }

     isSelectedTask(task) {
          return this.selectedTasks.indexOf(task.id) >= 0;
     }
     isSelectedGroup(group) {
          return this.selectedGroups.indexOf(group.id) >= 0;
     }

     resetAll() {
          this.selectedGroups = [];
          this.selectedTasks = [];
          this.resetCoaches();
          this.fetchPlanning();
     }

     openActivity($event: Event, scheduledTask) {
          const matchTask = this.taskList.find(
               (task) => task.id === scheduledTask.idActivity
          );
          if (!matchTask) {
               console.log(`Task not found.`);
               this.tvPopup = null;
               return;
          }
          const data = {
               scheduledTask,
               task: matchTask,
          };
          if (this.tvPopup?.info?.scheduledTask === scheduledTask) {
               return (this.tvPopup = null);
          }
          if (this.appCfg.tvScreen) {
               console.log($event);
               this.tvPopup = {
                    clickedElement: $event.currentTarget,
                    info: data,
               };
               setTimeout(() => {
                    let $target = $($event.target);
                    if (!$target.is('a')) {
                         $target = $target.closest('a');
                         if (!$target.length) {
                              return;
                         }
                    }
                    const $popup = $('#tvPopup');
                    ($popup as any).position({
                         of: $target,
                         my: 'left bottom-24',
                         at: 'center top',
                         using: (
                              output: { top: number; left: number },
                              extra: {
                                   element: {
                                        element: any;
                                        top: number;
                                        left: number;
                                        width: number;
                                        height: number;
                                   };
                                   vertical: 'top' | 'bottom' | 'center';
                                   horizontal: 'center' | 'left' | 'right';
                              }
                         ) => {
                              const toRemove = [];
                              $popup[0].classList.forEach((i) => {
                                   if (i.indexOf('pos_') === 0) {
                                        toRemove.push(i);
                                   }
                              });
                              $popup.removeClass(toRemove.join(' '));
                              $popup
                                   .css({
                                        top: output.top,
                                        left: output.left,
                                   })
                                   .addClass(
                                        'pos_' +
                                             extra.horizontal +
                                             '_' +
                                             extra.vertical
                                   );
                         },
                    });
               });
          } else {
               this.dialog
                    .open(BookComponent, {
                         data,
                         panelClass: 'dialog--no-padding',
                    })
                    .afterClosed();
          }
     }

     togglePanel(event, panel) {
          this.periodCollapse[panel] = !this.periodCollapse[panel];
          if (!this.periodCollapse[panel]) {
               setTimeout(
                    () =>
                         event.currentTarget?.scrollIntoView({
                              behavior: 'smooth',
                              block: 'end',
                              inline: 'nearest',
                         }),
                    200
               );
          }
     }

     closeTvPopup() {
          this.tvPopup = null;
     }
}
